import { type FC } from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import cn from '@/utils/classNames'

import styles from './Tabs.module.scss'

/**
 * Tabs Component
 *
 * A set of layered sections of content, known as tab panels, that are displayed one at a time.
 * Each tab has an associated trigger that controls the visibility of its content panel.
 *
 * Usage Example:
 * ```tsx
 * <Tabs defaultValue="tab1">
 *   <Tabs.List>
 *     <Tabs.Trigger value="tab1">Tab 1</Tabs.Trigger>
 *     <Tabs.Trigger value="tab2">Tab 2</Tabs.Trigger>
 *   </Tabs.List>
 *   <Tabs.Content value="tab1">
 *     Content for Tab 1
 *   </Tabs.Content>
 *   <Tabs.Content value="tab2">
 *     Content for Tab 2
 *   </Tabs.Content>
 * </Tabs>
 * ```
 */
const Tabs: FC<TabsProps> = (props) => {
  const { className, ...rest } = props
  const classList = cn(styles.tabs, className)

  return (
    <TabsPrimitive.Root className={classList} {...rest} />
  )
}

/**
 * Container component that holds the tab triggers.
 * Provides the visual grouping and layout for the tab navigation elements.
 */
const TabsList: FC<TabsPrimitive.TabsListProps> = (props) => {
  const { className, ...rest } = props
  const classList = cn(styles.tabsList, className)

  return (
    <TabsPrimitive.List className={classList} {...rest} />
  )
}

/**
 * Interactive element that controls which tab panel is displayed.
 * When clicked, it activates the corresponding tab content panel.
 */
const TabsTrigger: FC<TabsPrimitive.TabsTriggerProps> = (props) => {
  const { className, ...rest } = props
  const classList = cn(styles.tabsTrigger, className)

  return (
    <TabsPrimitive.Trigger className={classList} {...rest} />
  )
}

/**
 * Container for the content of a specific tab.
 * Only one content panel is visible at a time, corresponding to the active tab trigger.
 */
const TabsContent: FC<TabsPrimitive.TabsContentProps> = (props) => {
  const { className, ...rest } = props
  const classList = cn(styles.tabsContent, className)

  return (
    <TabsPrimitive.Content className={classList} {...rest} />
  )
}

export interface TabsProps extends TabsPrimitive.TabsProps {}

export default Object.assign(Tabs, {
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent
})
